import type { CustomPageReadType } from '@readme/api/src/mappings/custompage/types';
import type { HubResponseProps } from '@readme/iso';

import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';

import useClassy from '@core/hooks/useClassy';
import useRdmdOpts from '@core/hooks/useRdmdOpts';
import { InitializeSuperHubDocument, ConnectSuperHubDocumentToApi, useSuperHubStore } from '@core/store';
import { htmlBlock } from '@core/utils/magicBlocks';

import type { SuperHubRouteParams } from '@routes/SuperHub/types';

import RDMD from '@ui/RDMD';

import ConnectRoute from '../ConnectRoute';
import RedirectToProjectFirstPage from '../RedirectToProjectFirstPage';

import styles from './index.module.scss';

/**
 * Shape of route specific data for custom pages.
 */
export interface CustomPageRouteProps {
  apiDefinitions?: never;
  document: CustomPageReadType['data'];
}

function Content({ rdmd }: HubResponseProps<CustomPageRouteProps>) {
  const bem = useClassy(styles, 'SuperHubCustomPage');
  const customPage = useSuperHubStore(s => s.document.getCustomPageData());

  const content = useMemo(
    () =>
      customPage?.content.type === 'html' && customPage.content.body
        ? htmlBlock(customPage.content.body)
        : customPage?.content.body,
    [customPage?.content.body, customPage?.content.type],
  );

  // We need to render HTML mode using `md` format to ensure that the
  // legacy rdmd renderer is used because HTML mode uses custom blocks
  const isMdx = customPage?.content.type === 'markdown';
  const rdmdOpts = useRdmdOpts();

  return (
    <main className={bem('&', 'rm-CustomPage', customPage?.appearance.fullscreen && '_fullscreen')}>
      <div className={bem('-content')}>
        <header className={bem('-content-header')}>
          <h1 className={bem('-title')}>{customPage?.title}</h1>
        </header>
        <RDMD
          key={customPage?.slug}
          dehydrated={rdmd?.dehydrated?.body}
          format={isMdx ? 'mdx' : 'md'}
          opts={isMdx ? rdmdOpts : {}}
        >
          {content}
        </RDMD>
        <div className="ModalWrapper" id="tutorialmodal-root" />
      </div>
    </main>
  );
}

/**
 * Renders custom pages in "view" only mode. This also handles the rendering of
 * custom 404 error pages for projects that use them.
 */
export default function SuperHubCustomPage(props: HubResponseProps<CustomPageRouteProps>) {
  const { document: initialDocument } = props;
  const { slug } = useParams<SuperHubRouteParams>();

  // When route is /page with no slug, redirect to the project's first page only
  // if the request is not flagged as a 404. Otherwise, we are probably
  // rendering a custom 404 error page, which must not be redirected.
  if (!slug && !props.is404) {
    return <RedirectToProjectFirstPage />;
  }

  return (
    <InitializeSuperHubDocument document={initialDocument}>
      <ConnectSuperHubDocumentToApi>
        <ConnectRoute next={Content} props={props} />
      </ConnectSuperHubDocumentToApi>
    </InitializeSuperHubDocument>
  );
}
